// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * Who made this sheet, for whom, and which version of it you are holding.
 *
 * A logistics plan goes out. It is printed for the trailer wall, emailed to
 * the owner, attached to a permit application, handed to a sub at a
 * pre-install meeting — and then it goes out again next month with the
 * laydown moved. Without a title block the second sheet is
 * indistinguishable from the first, and the sub pours against the drawing he
 * happened to keep.
 *
 * So a block is needed that names the company that prepared the drawing, the
 * project and the sheet, and that is all it is: a label, not a record. What
 * belongs in it is this office's call, which is why every line of it can be
 * switched off.
 *
 * None of it is invented. A field nobody filled in is left off the sheet
 * rather than printed empty or guessed at, because a title block that states
 * something untrue is worse than one that is short.
 */

/**
 * A line the block can carry.
 *
 * Offices do not agree about what belongs in one. A sheet that goes to the
 * owner wants the client's name on it; the same sheet pinned in the trailer
 * does not, and a drawing of a house whose address is the project name says
 * it twice. So every line can be switched off, and what is left is what this
 * office puts on a drawing.
 */
export type BlockField =
  | "company" | "client" | "project" | "address" | "sheetTitle" | "date" | "scale";

/** The lines, in the order the block prints them, and what to call them. */
export const BLOCK_FIELDS: Array<{ key: BlockField; label: string }> = [
  { key: "company",    label: "Prepared by" },
  { key: "client",     label: "Prepared for" },
  { key: "project",    label: "Project" },
  { key: "address",    label: "Address" },
  { key: "sheetTitle", label: "Sheet" },
  { key: "date",       label: "Date" },
  { key: "scale",      label: "Scale" },
];

/** The part that belongs to the job rather than to one sheet. */
export interface TitleBlock {
  /** The company that prepared the drawing. */
  company?: string;
  /** One more line under it: address, phone, licence number. */
  companyLine?: string;
  /** Who it was prepared for. */
  client?: string;
  /**
   * The lines this office has switched off.
   *
   * Only the exceptions are stored, and absent means printed: a block saved
   * before any of this existed carries no map and keeps every line it had.
   */
  hidden?: Partial<Record<BlockField, true>>;
}

/** Whether a line is printed. Anything not switched off is. */
export function isShown(tb: TitleBlock | null | undefined, field: BlockField): boolean {
  return tb?.hidden?.[field] !== true;
}

/** The same block with one line switched the other way. */
export function toggleField(tb: TitleBlock | null | undefined, field: BlockField): TitleBlock {
  const base: TitleBlock = { ...(tb || {}) };
  const hidden = { ...(base.hidden || {}) };
  // Switched back on by dropping the flag rather than storing a false, so a
  // block that has never been touched stays empty.
  if (hidden[field]) delete hidden[field];
  else hidden[field] = true;
  if (Object.keys(hidden).length) base.hidden = hidden;
  else delete base.hidden;
  return base;
}

const clean = (v: unknown): string => String(v ?? "").trim();

/** "10/14/26" — how a revision block has always been dated. */
export function shortDate(iso: string | undefined): string {
  const s = clean(iso);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [y, m, d] = s.split("-").map(Number);
  return `${m}/${d}/${String(y).slice(2)}`;
}

/** Today, as the date fields want it. */
export function today(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

export interface BlockRow { label: string; value: string }

/**
 * The rows the title block actually prints.
 *
 * Empty fields are dropped rather than printed as a label with nothing after
 * it: a label with a blank beside it asserts something about the drawing --
 * that nobody checked it, that it has no client -- which may be true but is
 * not for the software to say on anyone's behalf.
 *
 * There is no sheet number, and no revision table. Numbering a sheet places
 * it in a set that an
 * engineer of record signs and seals, and a revision history is the record of
 * that set being reissued. A logistics plan is in neither; printing SL-1 and
 * REV B on it dresses it up as something it is not.
 */
export function blockRows(args: {
  tb?: TitleBlock | null;
  project?: string;
  address?: string;
  sheetTitle?: string;
  date?: string;
  scale?: string;
}): BlockRow[] {
  const rows: BlockRow[] = [];
  const add = (field: BlockField, label: string, value: unknown) => {
    const v = clean(value);
    if (v && isShown(args.tb, field)) rows.push({ label, value: v });
  };
  add("project", "PROJECT", args.project);
  add("address", "ADDRESS", args.address);
  add("client", "CLIENT", args.tb?.client);
  add("sheetTitle", "SHEET", args.sheetTitle);
  add("date", "DATE", shortDate(args.date));
  add("scale", "SCALE", args.scale);
  return rows;
}

/** Whether there is anything worth drawing a title block for. */
export function hasBlock(args: Parameters<typeof blockRows>[0], tb?: TitleBlock | null): boolean {
  return !!((clean(tb?.company) && isShown(tb, "company")) || blockRows({ ...args, tb }).length);
}
